import { useCallback, useEffect, useState } from "react";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, fmtDate, inr } from "@/lib/api";
import { useAdminApi, saveBlob } from "@/lib/admin";
import { useLoad, PageHead, DataTable, Td, NativeSelect } from "@/components/admin/kit";
import { StatusBadge } from "@/components/site/Notices";

const STATUSES = ["awaiting_payment", "payment_verification_pending", "paid", "processing", "packed", "shipped", "delivered", "cancelled", "refund_pending", "refunded"];
const SETTABLE = ["processing", "packed", "shipped", "delivered", "cancelled"];

const OrderDetail = ({ id, onClose }) => {
  const call = useAdminApi();
  const [o, setO] = useState(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(() => call("get", `/admin/orders/${id}`).then(setO), [call, id]);
  useEffect(() => {
    load();
  }, [load]);
  const save = async () => {
    try {
      await call("patch", `/admin/orders/${id}`, { status: status || null, note: note || null });
      toast.success("Order updated");
      setStatus("");
      setNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader><DialogTitle>{o ? `Invoice · ${o.order_number}` : "Loading…"}</DialogTitle></DialogHeader>
        {o && (
          <div className="print-area space-y-4 text-sm">
            <div className="flex flex-wrap gap-2"><StatusBadge status={o.status} testid="order-detail-status" /><StatusBadge status={o.payment_status} /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><p className="text-xs uppercase text-slate-500">Deliver to</p><p className="font-semibold">{o.customer.name}</p><p>{o.customer.address}{o.customer.landmark ? `, ${o.customer.landmark}` : ""}</p><p>{o.customer.city}, {o.customer.district}, {o.customer.state} – {o.customer.pincode}</p><p>{o.customer.mobile} · {o.customer.email}</p></div>
              <div><p className="text-xs uppercase text-slate-500">Payment</p>{o.payments.map((p) => <p key={p.id} className="font-mono-num text-xs">{p.razorpay_order_id} · {p.razorpay_payment_id || "—"} · {p.status}</p>)}<p>Receipt: {o.receipt_no || "—"}</p></div>
            </div>
            <table className="w-full"><tbody>{o.items.map((i) => <tr key={i.sku} className="border-b"><td className="py-1.5">{i.name} ({i.size}) <span className="font-mono-num text-xs text-slate-500">{i.sku}</span></td><td className="text-right">× {i.qty}</td><td className="text-right font-mono-num">{inr(i.line_total)}</td></tr>)}</tbody></table>
            <p className="text-right text-base font-bold">Total {inr(o.total)}</p>
            <div className="no-print space-y-2 rounded-xl bg-slate-50 p-4">
              <div className="flex flex-wrap gap-2"><NativeSelect value={status} onChange={setStatus} placeholder="Update fulfilment…" options={SETTABLE} testid="order-status-select" /></div>
              <Textarea placeholder="Internal note / tracking info" value={note} onChange={(e) => setNote(e.target.value)} data-testid="order-note-input" />
              <p className="text-xs text-slate-500">Fulfilment needs a verified payment. Cancelling a paid order restocks items; issue the refund from Payments & Refunds.</p>
              <div className="flex gap-2"><Button onClick={save} data-testid="order-save-button">Save</Button><Button variant="outline" onClick={() => window.print()} data-testid="order-invoice-print"><Printer className="h-4 w-4" /> Print invoice</Button></div>
            </div>
            <div>{o.history.map((h, i) => <p key={i} className="text-xs text-slate-500">{fmtDate(h.at, true)} → {h.status} ({h.by}){h.note ? ` – ${h.note}` : ""}</p>)}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Orders() {
  const call = useAdminApi();
  const [f, setF] = useState({ q: "", status: "", payment_status: "", page: 1 });
  const [applied, setApplied] = useState(f);
  const [data, reload] = useLoad("/admin/orders", applied);
  const [open, setOpen] = useState(null);
  const exportCsv = async () => {
    try {
      saveBlob(await call("get", "/admin/orders/export.csv", undefined, { params: applied, responseType: "blob" }), "orders.csv");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHead title="Product orders"><Button variant="outline" onClick={exportCsv} data-testid="orders-export-csv"><Download className="h-4 w-4" /> Download report</Button></PageHead>
      <div className="mb-4 flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-4">
        <Input className="h-9 w-64" placeholder="Order no., name, phone, email" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="orders-search-input" />
        <NativeSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="All statuses" options={STATUSES} testid="orders-filter-status" />
        <NativeSelect value={f.payment_status} onChange={(v) => setF({ ...f, payment_status: v })} placeholder="Any payment" options={["paid", "pending", "unpaid", "failed"]} testid="orders-filter-payment" />
        <Button className="h-9" onClick={() => setApplied({ ...f, page: 1 })} data-testid="orders-search-button">Search</Button>
      </div>
      <DataTable head={["Order", "Customer", "Items", "Total", "Payment", "Status", "Date"]} testid="orders-table">
        {data?.items.map((o) => (
          <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(o.id)} data-testid={`order-row-${o.order_number}`}>
            <Td className="font-mono-num text-xs">{o.order_number}</Td>
            <Td><p className="font-semibold">{o.customer.name}</p><p className="text-xs text-slate-500">{o.customer.mobile} · {o.customer.district}</p></Td>
            <Td className="text-xs">{o.items.map((i) => `${i.name} ${i.size} ×${i.qty}`).join(", ")}</Td>
            <Td className="font-mono-num">{inr(o.total)}</Td><Td><StatusBadge status={o.payment_status} /></Td><Td><StatusBadge status={o.status} /></Td><Td className="text-xs">{fmtDate(o.created_at, true)}</Td>
          </tr>
        ))}
      </DataTable>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" size="sm" disabled={applied.page <= 1} onClick={() => setApplied({ ...applied, page: applied.page - 1 })}>Previous</Button>
        <Button variant="outline" size="sm" disabled={!data || applied.page * 25 >= data.total} onClick={() => setApplied({ ...applied, page: applied.page + 1 })}>Next</Button>
      </div>
      {open && <OrderDetail id={open} onClose={() => { setOpen(null); reload(); }} />}
    </div>
  );
}
